import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_BASE_URL } from "../config";
import { setError } from "./errorSlice";
export const fetchTerms = createAsyncThunk(
  "term/fetchTerms",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }
      const response = await fetch(`${API_BASE_URL}/api/Term`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch terms");
      return await response.json();
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    }
  }
);

export const createTerm = createAsyncThunk(
  "term/createTerm",
  async (termData, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }
      const payload = {
        name: termData.name,
        academicYear: termData.academicYear,
        description: termData.description,
        state: true,
        createdDate: new Date().toISOString(),
        updatedDate: new Date().toISOString(),
      };

      const response = await fetch(`${API_BASE_URL}/api/Term/Create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create term");
      }

      return await response.json();
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    }
  }
);

export const updateTerm = createAsyncThunk(
  "term/updateTerm",
  async ({ id, ...termData }, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }
      const payload = {
        termId: id,
        term: {
          termId: id,
          name: termData.name,
          description: termData.description,
          academicYear: termData.academicYear,
          state: true,
          createdDate: termData.createdDate,
          updatedDate: new Date().toISOString(),
        },
      };

      const response = await fetch(`${API_BASE_URL}/api/Term/Update`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update term");
      }

      return await response.json();
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    }
  }
);

export const deleteTerm = createAsyncThunk(
  "term/deleteTerm",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }
      const response = await fetch(`${API_BASE_URL}/api/Term/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to delete term");
      return id;
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    }
  }
);

const termSlice = createSlice({
  name: "term",
  initialState: {
    terms: [],
    loading: false,
    error: null,
    editingTerm: null,
    modalVisible: false,
  },
  reducers: {
    setEditingTerm(state, action) {
      state.editingTerm = action.payload;
      state.modalVisible = true;
    },
    clearEditingTerm(state) {
      state.editingTerm = null;
      state.modalVisible = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch terms
      .addCase(fetchTerms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTerms.fulfilled, (state, action) => {
        state.loading = false;
        state.terms = action.payload;
      })
      .addCase(fetchTerms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create term
      .addCase(createTerm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTerm.fulfilled, (state, action) => {
        state.loading = false;
        state.terms.push(action.payload);
        state.editingTerm = null;
        state.modalVisible = false;
      })
      .addCase(createTerm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update term
      .addCase(updateTerm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTerm.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.terms.findIndex(
          (term) => term.termId === action.payload.termId
        );
        if (index !== -1) {
          state.terms[index] = action.payload;
        }
        state.editingTerm = null;
        state.modalVisible = false;
      })
      .addCase(updateTerm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete term
      .addCase(deleteTerm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTerm.fulfilled, (state, action) => {
        state.loading = false;
        state.terms = state.terms.filter(
          (term) => term.termId !== action.payload
        );
      })
      .addCase(deleteTerm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setEditingTerm, clearEditingTerm } = termSlice.actions;
export default termSlice.reducer;
